import { useOutletContext, useSearchParams } from 'react-router-dom'

import { Box, Button, Card, ProgressBar, Text } from '@factorialco/deprecated-design-system'
import { PerformanceReviewOverviewParticipationRateTaskDocument } from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { SendRemindersPanel } from 'modules/performance_process/components/insights/SendRemindersPanel'
import { useFiltersValues } from 'modules/performance_process/components/review/show/Filters/context'
import { TaskCard } from 'modules/performance_process/components/review/show/Tasks/TaskCard'
import { ReviewOverviewOutletContext } from 'modules/performance_process/pages/reviews/show/overview'

interface Props {
  search?: string
}

export const ParticipationRateTask = ({ search }: Props) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const isSidepanelOpen = searchParams.get('sendRemindersPanel') === 'open'
  const context = useOutletContext<ReviewOverviewOutletContext>()

  const {
    employeeIds,
    managerId,
    onlyDirectReports,
    agreementStatus,
    scores,
    teamIds,
    tenureDates,
    workplaceIds,
    legalEntityIds,
  } = useFiltersValues()

  const { data, loading } = useQuery(PerformanceReviewOverviewParticipationRateTaskDocument, {
    variables: {
      id: context.reviewProcess.id,
      employeeIds,
      managedByFilter: managerId
        ? {
            managerEmployeeId: managerId,
            onlyDirectReports: onlyDirectReports ?? true,
          }
        : undefined,
      search: search || undefined,
      agreementStatus,
      scores,
      teamIds,
      tenureDates,
      workplaceIds,
      legalEntityIds,
    },
  })

  const reviewProcess = data?.performance.reviewProcess

  if (loading || !reviewProcess) return null

  const evaluationsCount = reviewProcess.participation?.evaluationsCount
  const publishedEvaluationsCount = reviewProcess.participation?.publishedEvaluationsCount
  const hasParticipationRateInfo = evaluationsCount != null && publishedEvaluationsCount != null

  if (!hasParticipationRateInfo || evaluationsCount === 0) return null

  const hasPendingEvaluations = publishedEvaluationsCount < evaluationsCount
  const canRemind = reviewProcess.status !== 'finished' && hasPendingEvaluations
  const participationPercentage = Math.round((publishedEvaluationsCount / evaluationsCount) * 100)

  const onClick = () => setSearchParams({ sendRemindersPanel: 'open' })

  return (
    <>
      <TaskCard onClick={hasPendingEvaluations ? onClick : undefined}>
        <Card.Title size="small">
          {i18n.t('performance_process.overview.completion_rate')}
        </Card.Title>
        <Card.Content>
          <Box
            gap="s16"
            width="full"
            flexGrow
            justifyContent="spaceBetween"
            data-testid="participation-rate-task"
          >
            <Box flexDirection="row" gap="s8">
              <Text.Header1 weight="semibold">{participationPercentage}%</Text.Header1>
              <ProgressBar
                total={evaluationsCount}
                amount={publishedEvaluationsCount}
                barColor="grey400"
                progressColor="viridian1000"
                showLabel
              />
            </Box>
            {hasPendingEvaluations && (
              <Button onClick={onClick} type="secondary">
                {canRemind
                  ? i18n.t('performance_process.overview.participation.send_reminders.title')
                  : i18n.t('performance_process.overview.tasks.view_all')}
              </Button>
            )}
          </Box>
        </Card.Content>
      </TaskCard>

      {isSidepanelOpen && (
        <SendRemindersPanel
          reviewId={reviewProcess.id}
          onClose={() => setSearchParams({})}
          lastReminder={reviewProcess.lastBulkReminder}
          canRemind={canRemind}
        />
      )}
    </>
  )
}
